import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/common";
import { Ban } from "lucide-react";

export default function PortalLoginPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/companies/by-slug/${slug}`)
      .then(({ data }) => setCompany(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password, slug);
      navigate("/portal");
    } catch (err) {
      setError(fmtErr(err, "Connexion impossible"));
    } finally {
      setLoading(false);
    }
  };

  if (notFound) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background p-6" data-testid="portal-not-found">
        <div className="text-center">
          <h1 className="text-2xl font-semibold">Portail introuvable</h1>
          <p className="mt-2 text-sm text-muted-foreground">Vérifiez le lien communiqué par votre prestataire.</p>
        </div>
      </div>
    );
  }

  if (company?.status === "suspended" || company?.status === "archived") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background p-6" data-testid="portal-suspended">
        <div className="max-w-md rounded-sm border border-border bg-card p-10 text-center">
          <Ban className="mx-auto h-10 w-10 text-primary" />
          <h1 className="mt-6 text-2xl font-semibold">Espace suspendu</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            L'espace client de <b>{company.name}</b> est temporairement suspendu. Vos données sont conservées.
            Contactez votre prestataire pour plus d'informations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-6" data-testid="portal-login-page">
      <div className="w-full max-w-md">
        <div className="rounded-sm border border-border bg-card p-8 sm:p-10">
          <div className="font-heading text-xl font-semibold">Suivio<span className="text-primary">360</span></div>
          <h1 className="mt-6 text-2xl font-semibold tracking-tight" data-testid="portal-company-name">{company ? company.name : "…"}</h1>
          <p className="mt-2 text-sm text-muted-foreground">Connectez-vous à votre espace client pour suivre vos demandes.</p>
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="p-email">Email</Label>
              <Input id="p-email" data-testid="portal-login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="p-password">Mot de passe</Label>
                <Link to={`/mot-de-passe-oublie?portal=${slug}`} data-testid="portal-forgot-password-link"
                  className="text-xs font-medium text-primary hover:underline">Mot de passe oublié ?</Link>
              </div>
              <PasswordInput id="p-password" testid="portal-login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            {error && <div data-testid="portal-login-error" className="rounded-sm border border-primary/40 bg-[hsl(var(--note-bg))] px-4 py-3 text-sm text-primary">{error}</div>}
            <Button data-testid="portal-login-submit-button" type="submit" disabled={loading || !company} className="w-full rounded-sm bg-primary text-white hover:bg-red-700 transition-colors duration-200">
              {loading ? "Connexion…" : "Accéder à mon espace"}
            </Button>
          </form>
        </div>
        <p className="mt-6 text-center text-xs text-muted-foreground">
          Équipe {company?.name || ""} ? <Link to="/login" data-testid="portal-staff-login-link" className="text-primary hover:underline">Connexion équipe</Link>
        </p>
      </div>
    </div>
  );
}
